import React, { useCallback, useEffect, useMemo, useRef } from "react";

const emptyValues = () => Object.create(null);

const serverCapabilitiesRemainDisabled = (preview) => {
  const capabilities = preview?.capabilities;
  const safety = preview?.safety;
  return Boolean(
    capabilities
    && capabilities.answerEntryAllowed === false
    && capabilities.fileUploadAllowed === false
    && capabilities.saveAllowed === false
    && capabilities.submitAllowed === false
    && capabilities.bookingAllowed === false
    && capabilities.crmWriteAllowed === false
    && safety
    && safety.providerMode === "disabled"
    && safety.providerCallsAllowed === false
    && safety.emailSent === false
    && safety.executionAllowed === false
    && safety.claimable === false
    && safety.queueState === "not_queued"
    && safety.deliveryStatus === "not_sent"
  );
};

const localInteractionIsExact = (preview) => {
  const interaction = preview?.localInteraction;
  return Boolean(
    preview?.schemaVersion === "campaign_uat_public_questionnaire_preview.v2"
    && serverCapabilitiesRemainDisabled(preview)
    && interaction
    && interaction.schemaVersion === "campaign_uat_public_local_interaction.v1"
    && interaction.mode === "client_memory_text_only"
    && interaction.allowed === true
    && Array.isArray(interaction.supportedResponseTypes)
    && interaction.supportedResponseTypes.length === 1
    && interaction.supportedResponseTypes[0] === "text"
    && interaction.maxCharacters === 200
    && interaction.answerTransmissionAllowed === false
    && interaction.answerPersistenceAllowed === false
    && interaction.browserStorageAllowed === false
    && interaction.telemetryAllowed === false
  );
};

const eligibleTextQuestions = (preview) => {
  if (
    !localInteractionIsExact(preview)
    || !Array.isArray(preview?.questionnaire?.sections)
    || preview.questionnaire.sections.some(
      (section) => !Array.isArray(section?.questions),
    )
  ) return [];
  return preview.questionnaire.sections.flatMap((section) =>
    section.questions
      .filter((question) => (
        question.responseType === "text"
        && question.conditionalRequired === false
        && question.conditionalVisibility === false
      ))
      .map((question) => ({ section, question })),
  );
};

export default function CampaignLocalTextInteractionRehearsal({ preview }) {
  const questions = useMemo(() => eligibleTextQuestions(preview), [preview]);
  const valuesRef = useRef(emptyValues());
  const inputNodesRef = useRef(new Map());

  const purgeLocalValues = useCallback(() => {
    valuesRef.current = emptyValues();
    inputNodesRef.current.forEach((node) => {
      if (node) node.value = "";
    });
  }, []);

  useEffect(() => {
    // pagehide runs before a page can enter the back/forward cache. Directly
    // clearing both the memory map and the DOM makes the purge synchronous;
    // pageshow repeats it before a restored page can be used again.
    window.addEventListener("pagehide", purgeLocalValues);
    window.addEventListener("pageshow", purgeLocalValues);
    return () => {
      window.removeEventListener("pagehide", purgeLocalValues);
      window.removeEventListener("pageshow", purgeLocalValues);
      purgeLocalValues();
    };
  }, [purgeLocalValues]);

  if (questions.length === 0) return null;

  return (
    <section
      aria-labelledby="campaign-local-interaction-title"
      className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50 p-5"
      data-testid="campaign-local-text-interaction-rehearsal"
    >
      <h2
        id="campaign-local-interaction-title"
        className="text-lg font-semibold text-cyan-950"
      >
        Local interaction rehearsal
      </h2>
      <p className="mt-2 text-sm text-cyan-950">
        Synthetic test values only. Text typed here stays only in this page&apos;s
        memory, is never saved or transmitted, and clears when this page is left,
        restored from browser history, closed, refreshed, or the verified session
        expires.
      </p>
      <p className="mt-1 text-xs text-cyan-900">
        This is not questionnaire answer entry. Submission, uploads, booking,
        and CRM changes remain unavailable.
      </p>

      <div className="mt-4 space-y-4">
        {questions.map(({ section, question }) => {
          const coordinate = `${section.sequence}:${question.sequence}`;
          const inputId = `campaign-local-text-${section.sequence}-${question.sequence}`;
          return (
            <div
              className="rounded-lg border border-cyan-200 bg-white p-4"
              key={coordinate}
            >
              <label
                className="block text-sm font-medium text-slate-900"
                htmlFor={inputId}
              >
                {question.label}
              </label>
              <input
                aria-describedby={`${inputId}-notice`}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
                defaultValue=""
                id={inputId}
                maxLength={preview.localInteraction.maxCharacters}
                onChange={(event) => {
                  const value = event.currentTarget.value.slice(
                    0,
                    preview.localInteraction.maxCharacters,
                  );
                  if (event.currentTarget.value !== value) {
                    event.currentTarget.value = value;
                  }
                  valuesRef.current[coordinate] = value;
                }}
                onCopy={(event) => event.preventDefault()}
                onCut={(event) => event.preventDefault()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                onPaste={(event) => event.preventDefault()}
                ref={(node) => {
                  if (node) inputNodesRef.current.set(coordinate, node);
                  else inputNodesRef.current.delete(coordinate);
                }}
                spellCheck={false}
                type="text"
              />
              <p className="mt-1 text-xs text-slate-600" id={`${inputId}-notice`}>
                Local memory only; nothing is saved or sent.
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
